import { z } from 'zod';
import { AZURE_CLOUDS } from './cloud.js';

/**
 * Type-safe environment.
 *
 * **Never read `import.meta.env` directly** — go through `env` from this
 * module. Validation runs at module load, so a missing variable is a loud
 * startup failure instead of `undefined` surfacing as a broken fetch three
 * screens later.
 *
 * Only `VITE_`-prefixed variables are exposed to the client by Vite. Anything
 * else is a build-time leak waiting to happen, so the schema only names
 * `VITE_` keys.
 *
 * Adding a variable is two steps in one PR: extend the schema here, and add a
 * placeholder with its prose to `apps/web/.env.example`. (There is no
 * `apps/web/README.md`; this comment used to point at a table in one. The
 * variables are documented in `.env.example`, which is the file a developer
 * opens, and one place for one fact is Article 3.)
 */

const booleanish = z
  .enum(['true', 'false', '1', '0'])
  .transform((value) => value === 'true' || value === '1');

const baseEnvSchema = z.object({
  /**
   * Where the API is, including its route prefix.
   *
   * **Either an absolute URL or a same-origin path**, and which one it is
   * decides whether a Blue Partner session can exist at all (SYS-8, SYS-10). A
   * session is an `HttpOnly` cookie, and a cookie set by
   * `func-bpf-d.azurewebsites.net` is never sent to a page served from
   * `stbpfwebd.z13.web.core.windows.net` — those are different *sites*, not
   * merely different origins, and no configuration makes them one.
   *
   * So `/api` is the target shape: the SPA and the API behind one hostname,
   * which is also what fixes the WebAuthn relying-party id permanently.
   *
   * **An absolute URL no longer authenticates by itself.** This paragraph used
   * to say the API accepts an Entra bearer as well as a cookie; ADR-0058 deleted
   * every reader of one, so a two-origin build signs nobody in unless
   * `VITE_SIMULATED_AUTH` is set and it carries the session handle itself
   * (ADR-0059). Still permissive rather than a flag day — the value is accepted
   * — but the consequence of choosing it is now total rather than partial.
   */
  VITE_API_BASE_URL: z
    .string()
    .min(1, 'is required — e.g. /api, or http://localhost:7071/api')
    .refine(
      (value) => value.startsWith('/') || URL.canParse(value),
      'must be an absolute URL, or a path beginning with /',
    ),

  /** Serve MSW mocks in the browser. Default true — constitution Article 5. */
  VITE_USE_MOCKS: booleanish.default(true),

  /**
   * Carry the session handle ourselves, because no cookie can exist
   * (**ADR-0059**).
   *
   * **The browser half of the API's `SIMULATED_AUTH`, and the two must agree.**
   * That is the one genuinely awkward thing about this setting and it is stated
   * rather than hidden: ADR-0026's rule is that two values which must agree are
   * two values that will eventually disagree, and here one lives in a bundle and
   * the other in a Function App's settings, so they cannot be one value. What
   * makes it survivable is that both derive from a single field on
   * `scripts/lib/azure.mjs`'s environment row, and that each direction of
   * disagreement fails visibly:
   *
   * - API on, SPA off — the landing page still says sign-in is unavailable,
   *   because `canHoldSession` was never told otherwise.
   * - SPA on, API off — sign-in appears to succeed and every request afterwards
   *   answers 401, because the header nothing licensed is not a credential.
   *
   * `GET /api/health` reports the API's half, which is how to tell the two
   * apart in a deployment rather than by guessing.
   *
   * **Never set this for an environment holding real agency data.** The handle
   * lives in `sessionStorage` and is readable by JavaScript, unlike the
   * `HttpOnly` cookie it stands in for. The API refuses to start with its half
   * enabled anywhere but the deployments named in `SIMULATED_AUTH_SITES`, which
   * is the interlock that actually enforces this; the sentence is here so the
   * next person to copy an `.env` file knows why.
   */
  VITE_SIMULATED_AUTH: booleanish.default(false),

  /**
   * The SPA's own app registration (ADR-0027).
   *
   * Optional under mocks, required otherwise — see the refinement below. None
   * of these three is a secret: Vite inlines them into a bundle served
   * anonymously from `$web`, so they are as public as `view-source`.
   */
  VITE_ENTRA_CLIENT_ID: z.uuid('must be a GUID').optional(),

  /** The directory the SPA signs in against (SYS-5). */
  VITE_ENTRA_TENANT_ID: z.uuid('must be a GUID').optional(),

  /**
   * The API's **full** exposed-scope URI, e.g.
   * `api://{apiClientId}/access_as_user` — not the API's client id.
   *
   * The API asserts only that `scp` is present and non-empty (ADR-0004), so the
   * scope's *name* is a fact about its registration that this app must not
   * assume. One value copied from the portal beats two combined here.
   */
  VITE_ENTRA_API_SCOPE: z
    .string()
    .min(1)
    .refine((value) => !/\s/.test(value), 'must be a single scope — a space requests two')
    .optional(),

  /**
   * Which Azure the deployment signs in against, mirroring `AZURE_CLOUD` on the
   * API (ADR-0026). Defaults to commercial, which is what MSAL assumes anyway,
   * so a commercial deployment configures nothing and government says so.
   */
  VITE_AZURE_CLOUD: z.enum(AZURE_CLOUDS).default('AzureCloud'),

  /**
   * The sign-in authority, **stated rather than derived** (ADR-0039).
   *
   * Normally absent, and absent is the better case: a workforce directory's
   * authority is `login.microsoftonline.{com,us}/{tenantId}`, a function of the
   * cloud and the tenant, and stating it would be a second value free to
   * disagree with the first — the trap ADR-0026 exists for.
   *
   * Entra External ID breaks that derivation. It issues from
   * `{tenantId}.ciamlogin.com`, which is a fact about the **directory** rather
   * than the cloud, and nothing else here knows it. So this is the escape
   * hatch for a directory whose authority cannot be computed, and it wins over
   * the derived form when present.
   */
  VITE_ENTRA_AUTHORITY: z.url('VITE_ENTRA_AUTHORITY must be an absolute URL').optional(),

  /**
   * Whether the mocked build starts signed in. Mocks only; ignored otherwise.
   *
   * Default `true`, so the dev loop and all three e2e specs land on the app as
   * they always have. Set it to `false` to reach the landing page itself — the
   * Sign in button and the redirect hold — without an Entra tenant, which is
   * the only way to work on that screen at all (Article 5).
   */
  VITE_MOCK_SIGNED_IN: booleanish.default(true),
});

/**
 * Cross-field rules.
 *
 * As on the API (`apps/api/src/shared/config.ts`), this produces a `ZodEffects`
 * rather than a `ZodObject`, so `.extend()` is no longer available on the
 * result. Add fields to `baseEnvSchema` above.
 */
const envSchema = baseEnvSchema.superRefine((env, ctx) => {
  // Article 5: a mocked build signs nobody in, so it needs no Entra
  // configuration at all — not even a placeholder.
  if (env.VITE_USE_MOCKS) return;

  for (const key of [
    'VITE_ENTRA_CLIENT_ID',
    'VITE_ENTRA_TENANT_ID',
    'VITE_ENTRA_API_SCOPE',
  ] as const) {
    if (env[key] === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: [key],
        message: 'is required when VITE_USE_MOCKS=false — the SPA signs in with Entra (SYS-8).',
      });
    }
  }
});

export type Env = z.infer<typeof envSchema>;

function formatIssues(error: z.ZodError): string {
  return error.issues
    .map((issue) => `  - ${issue.path.join('.') || '(root)'} ${issue.message}`)
    .join('\n');
}

/**
 * Validate a raw env record. Exported so tests can exercise validation without
 * touching `import.meta.env`.
 *
 * @throws if any required variable is missing or malformed.
 */
export function parseEnv(source: Record<string, unknown>, appDir?: string): Env {
  const result = envSchema.safeParse(source);

  if (!result.success) {
    // **`appDir` is optional because this package serves more than one app**,
    // and it only ever qualifies the paths — the sentence itself is always
    // there, because naming the file to open is the most useful half of this
    // error and `env.test.ts` holds it. The directory was hardcoded to
    // `apps/web`, which was right while there was one app and would have been a
    // lie the moment there were two.
    const dir = appDir === undefined ? '' : `${appDir}/`;

    throw new Error(
      `Invalid environment configuration:\n${formatIssues(result.error)}\n\n` +
        `Fix ${dir}.env.local and restart the dev server. ` +
        `Start from ${dir}.env.example — only VITE_-prefixed variables reach the client.`,
    );
  }

  return result.data;
}
